import React from 'react';
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { Input } from './input';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./dropdown-menu";
import { TaskPriority } from '../../types/task';
import { cn } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

export interface SearchFilters {
  priority?: TaskPriority;
}

interface SearchBarProps {
  onSearch: (query: string, filters?: SearchFilters) => void;
  placeholder?: string;
  priorities?: TaskPriority[];
  className?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({ 
  onSearch, 
  placeholder = "Search...",
  priorities = [],
  className
}) => {
  const [filters, setFilters] = React.useState<SearchFilters>({});
  const [query, setQuery] = React.useState('');

  const getPriorityColor = (priority: TaskPriority | 'none') => {
    switch (priority) {
      case 'urgent': return 'text-red-600';
      case 'high': return 'text-orange-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      case 'none': return 'text-gray-500';
      default: return '';
    }
  };

  const handleSearch = (newQuery: string) => {
    setQuery(newQuery);
    onSearch(newQuery, filters);
  };

  const handleFilterChange = (priority: TaskPriority | 'none') => {
    let newFilters = { ...filters };

    if (priority === 'none' || filters.priority === priority) {
      newFilters = {};
    } else {
      newFilters.priority = priority as TaskPriority;
    }

    setFilters(newFilters);
    onSearch(query, newFilters);
  };

  return (
    <div className={`relative flex gap-2 ${className}`}>
      <div className="relative flex-1">
        <Input
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={placeholder}
          className="pl-9 w-full"
        />
        <MagnifyingGlassIcon 
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" 
        />
      </div>
      
      <DropdownMenu>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon"
                  className={filters.priority ? "text-primary" : ""}
                >
                  <FunnelIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>{filters.priority ? `Filtering by ${filters.priority} priority` : 'Filter tasks by priority'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Filter by Priority
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {[...priorities, 'none'].map((priority) => (
            <DropdownMenuItem
              key={priority}
              onClick={() => handleFilterChange(priority as TaskPriority)}
              className={cn(
                "flex items-center justify-between capitalize",
                getPriorityColor(priority as TaskPriority | 'none')
              )}
            >
              <span className="flex items-center gap-3">
                <span className={cn(
                  "h-2.5 w-2.5 rounded-full",
                  priority === 'urgent' && "bg-red-500",
                  priority === 'high' && "bg-orange-500",
                  priority === 'medium' && "bg-yellow-500",
                  priority === 'low' && "bg-green-500",
                  priority === 'none' && "bg-gray-400",
                )} />
                <span>Priority: {priority}</span>
              </span>
              {filters.priority === priority && (
                <span className="text-primary ml-2">✓</span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}; 